import React, { useCallback, useEffect, useRef, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import "./PWPagesV2.css";
import { useSiteContext } from "../SiteContext";
import { Page } from "../interfaces/PagesInterface";
import config from "../configLoader";
import {
    createPWFringeType,
    deactivatePWFringeType,
    activatePWFringeType,
    getPWFringeTypes,
    patchPWFringeType,
    getPWFringeOutputGroups,
    createPWFringeOutputGroup,
    patchPWFringeOutputGroup,
    deletePWFringeOutputGroup,
    getErrorMessage,
} from "../api/prevailingWage";
import { PWFringeType, PWFringeOutputGroup } from "../interfaces/PrevailingWage";
import {
    BadgeDollarSign,
    Box,
    ChevronRight,
    ChevronUp,
    Info,
    Check,
    Slash,
    Edit,
    Plus,
    Trash2,
    X,
    AlertTriangle,
    CheckCircle,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type TabId = "types" | "groups";
type FtSortKey = "sort" | "name" | "desc";
type FilterActive = "active" | "inactive";

interface ToastItem {
    id: number;
    kind: "ok" | "err";
    msg: string;
    undoFn?: () => void;
    out?: boolean;
}

interface DeleteGroupModal {
    group: PWFringeOutputGroup;
    conflict: PWFringeType[];
}

const PAGE_SIZES = [15, 30, 50, 100];
const DASH = "—";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function cmp(a: string | number | null | undefined, b: string | number | null | undefined): number {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function pageWindow(p: number, pages: number): (number | "…")[] {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const out: (number | "…")[] = [1];
    const lo = Math.max(2, p - 1);
    const hi = Math.min(pages - 1, p + 1);
    if (lo > 2) out.push("…");
    for (let i = lo; i <= hi; i++) out.push(i);
    if (hi < pages - 1) out.push("…");
    out.push(pages);
    return out;
}

// ─── Component ───────────────────────────────────────────────────────────────

const PWFringeTypesPage: React.FC = () => {
    const { setLoading, createPopup, userRole, setPage } = useSiteContext();
    const isReadOnly = config.readOnlyPrevailingWagePages;

    // ── Tab ───────────────────────────────────────────────────────────────
    const [tab, setTab] = useState<TabId>("types");

    // ── Fringe types data ─────────────────────────────────────────────────
    const [items, setItems] = useState<PWFringeType[] | null>(null);
    const [groups, setGroups] = useState<PWFringeOutputGroup[] | null>(null);

    // ── Filter / sort / page (fringe types tab) ───────────────────────────
    const [filterActive, setFilterActive] = useState<FilterActive>("active");
    const [sortKey, setSortKey] = useState<FtSortKey>("sort");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(30);

    // ── Fringe type modal ─────────────────────────────────────────────────
    const [ftModalOpen, setFtModalOpen] = useState(false);
    const [ftEditing, setFtEditing] = useState<PWFringeType | null>(null);
    const [ftName, setFtName] = useState("");
    const [ftNameErr, setFtNameErr] = useState(false);
    const [ftDesc, setFtDesc] = useState("");
    const [ftSort, setFtSort] = useState("");
    const [ftTC, setFtTC] = useState<number | null>(null);
    const [ftPE, setFtPE] = useState<number | null>(null);
    const [ftAE, setFtAE] = useState<number | null>(null);
    const [ftDG, setFtDG] = useState<number | null>(null);
    const [ftSaving, setFtSaving] = useState(false);

    // ── Output group modal ────────────────────────────────────────────────
    const [grpModalOpen, setGrpModalOpen] = useState(false);
    const [grpEditing, setGrpEditing] = useState<PWFringeOutputGroup | null>(null);
    const [grpName, setGrpName] = useState("");
    const [grpNameErr, setGrpNameErr] = useState(false);
    const [grpPay, setGrpPay] = useState("");
    const [grpSort, setGrpSort] = useState("");
    const [grpSaving, setGrpSaving] = useState(false);

    // ── Delete confirm modal ──────────────────────────────────────────────
    const [deleteModal, setDeleteModal] = useState<DeleteGroupModal | null>(null);
    const [deleting, setDeleting] = useState(false);

    // ── Toasts ────────────────────────────────────────────────────────────
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const toastIdRef = useRef(0);

    // ── Access gate ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!userRole.admin) {
            createPopup("Access Denied", "You do not have permission to access this page.");
            setPage(Page.Dashboard);
            return;
        }
        if (!config.showPrevailingWagePages) {
            createPopup("Access Denied", "This feature is not available.");
            setPage(Page.Dashboard);
        }
    }, [userRole, setPage, createPopup]);

    // ── Load ──────────────────────────────────────────────────────────────
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ftData, grpData] = await Promise.all([
                getPWFringeTypes(false),
                getPWFringeOutputGroups(),
            ]);
            setItems(ftData);
            setGroups(grpData);
        } catch (e) {
            console.error(e);
            createPopup("Error", getErrorMessage(e));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createPopup/setLoading unstable from SiteContext; including them recreates load every provider render and the load effect refires forever (infinite loading)
    }, []);

    const loadGroups = useCallback(async () => {
        try {
            const grpData = await getPWFringeOutputGroups();
            setGroups(grpData);
        } catch (e) {
            console.error(e);
            createPopup("Error", getErrorMessage(e));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- createPopup unstable from SiteContext (see load above)
    }, []);

    useEffect(() => {
        if (!userRole.admin || !config.showPrevailingWagePages) return;
        load();
    }, [userRole.admin, load]);

    // ── Toast ─────────────────────────────────────────────────────────────
    const showToast = useCallback((kind: "ok" | "err", msg: string, undoFn?: () => void) => {
        const id = ++toastIdRef.current;
        setToasts((prev) => [...prev, { id, kind, msg, undoFn }]);
        const duration = undoFn ? 5200 : 3400;
        setTimeout(() => {
            setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, out: true } : t)));
            setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 220);
        }, duration);
    }, []);

    // ── Derived: sorted groups ────────────────────────────────────────────
    const sortedGroups = (groups ?? []).slice().sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.name.localeCompare(b.name);
    });

    const groupById = (id: number | null): PWFringeOutputGroup | undefined => {
        if (id === null) return undefined;
        return (groups ?? []).find((g) => g.pwFringeOutputGroupID === id);
    };

    // ── Fringe types: filter / sort / page ───────────────────────────────
    const handleFtSort = (key: FtSortKey) => {
        if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir("asc"); }
        setPageNum(1);
    };

    const handleFtFilter = (v: FilterActive) => {
        setFilterActive(v);
        setPageNum(1);
    };

    const filteredFt = (items ?? []).filter((x) =>
        filterActive === "active" ? x.active : !x.active
    );
    const keyFns: Record<FtSortKey, (f: PWFringeType) => string | number> = {
        sort: (f) => f.sortOrder,
        name: (f) => f.name.toLowerCase(),
        desc: (f) => (f.description ?? "").toLowerCase(),
    };
    const sortedFt = filteredFt.slice().sort((a, b) => {
        const av = keyFns[sortKey](a);
        const bv = keyFns[sortKey](b);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return (sortDir === "asc" ? 1 : -1) * cmp(av, bv);
    });
    const total = sortedFt.length;
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.min(pageNum, pages);
    const viewFt = sortedFt.slice((safePage - 1) * pageSize, safePage * pageSize);

    // Active count for tab badge
    const activeTypeCount = (items ?? []).filter((x) => x.active).length;
    const groupCount = (groups ?? []).length;

    // ── Fringe type modal handlers ────────────────────────────────────────
    const openCreateFt = () => {
        setFtEditing(null);
        setFtName(""); setFtNameErr(false); setFtDesc(""); setFtSort("");
        setFtTC(null); setFtPE(null); setFtAE(null); setFtDG(null);
        setFtModalOpen(true);
    };

    const openEditFt = (row: PWFringeType) => {
        setFtEditing(row);
        setFtName(row.name); setFtNameErr(false);
        setFtDesc(row.description ?? "");
        setFtSort(row.sortOrder != null ? String(row.sortOrder) : "");
        setFtTC(row.timecardGroupID ?? null);
        setFtPE(row.payrollExportGroupID ?? null);
        setFtAE(row.accountingExportGroupID ?? null);
        setFtDG(row.deductionPayrollExportGroupID ?? null);
        setFtModalOpen(true);
    };

    const closeFtModal = () => { setFtModalOpen(false); setFtEditing(null); };

    const saveFt = async () => {
        const trimName = ftName.trim();
        if (!trimName) { setFtNameErr(true); return; }
        setFtNameErr(false);
        const sortNum = ftSort.trim() !== "" ? parseInt(ftSort, 10) : undefined;
        setFtSaving(true);
        setLoading(true);
        try {
            if (ftEditing) {
                const updated = await patchPWFringeType(ftEditing.pwFringeTypeID, {
                    name: trimName,
                    description: ftDesc.trim() || null,
                    sortOrder: sortNum,
                    timecardGroupID: ftTC,
                    payrollExportGroupID: ftPE,
                    accountingExportGroupID: ftAE,
                    deductionPayrollExportGroupID: ftDG,
                });
                setItems((prev) =>
                    prev?.map((x) =>
                        x.pwFringeTypeID === ftEditing.pwFringeTypeID ? { ...x, ...updated } : x
                    ) ?? prev
                );
                showToast("ok", `Fringe type <b>${trimName}</b> updated.`);
            } else {
                const created = await createPWFringeType({
                    name: trimName,
                    description: ftDesc.trim() || null,
                    sortOrder: sortNum,
                    timecardGroupID: ftTC,
                    payrollExportGroupID: ftPE,
                    accountingExportGroupID: ftAE,
                    deductionPayrollExportGroupID: ftDG,
                });
                setItems((prev) => (prev ? [...prev, created] : [created]));
                showToast("ok", `Fringe type <b>${trimName}</b> added.`);
            }
            closeFtModal();
        } catch (err) {
            showToast("err", getErrorMessage(err));
        } finally {
            setFtSaving(false);
            setLoading(false);
        }
    };

    // ── Toggle fringe type active with undo ───────────────────────────────
    const handleToggleFt = useCallback(
        async (row: PWFringeType) => {
            const wasActive = row.active;
            setItems((prev) =>
                prev?.map((x) =>
                    x.pwFringeTypeID === row.pwFringeTypeID ? { ...x, active: !wasActive } : x
                ) ?? prev
            );
            try {
                if (wasActive) await deactivatePWFringeType(row.pwFringeTypeID);
                else await activatePWFringeType(row.pwFringeTypeID);
                showToast(
                    "ok",
                    `<b>${row.name}</b> ${wasActive ? "deactivated" : "activated"}.`,
                    async () => {
                        try {
                            if (wasActive) await activatePWFringeType(row.pwFringeTypeID);
                            else await deactivatePWFringeType(row.pwFringeTypeID);
                            setItems((prev) =>
                                prev?.map((x) =>
                                    x.pwFringeTypeID === row.pwFringeTypeID
                                        ? { ...x, active: wasActive }
                                        : x
                                ) ?? prev
                            );
                        } catch (err) {
                            showToast("err", getErrorMessage(err));
                        }
                    }
                );
            } catch (err) {
                setItems((prev) =>
                    prev?.map((x) =>
                        x.pwFringeTypeID === row.pwFringeTypeID ? { ...x, active: wasActive } : x
                    ) ?? prev
                );
                showToast("err", getErrorMessage(err));
            }
        },
        [showToast]
    );

    // ── Output group modal handlers ───────────────────────────────────────
    const openCreateGrp = () => {
        setGrpEditing(null);
        setGrpName(""); setGrpNameErr(false); setGrpPay(""); setGrpSort("");
        setGrpModalOpen(true);
    };

    const openEditGrp = (grp: PWFringeOutputGroup) => {
        setGrpEditing(grp);
        setGrpName(grp.name); setGrpNameErr(false);
        setGrpPay(grp.payType ?? "");
        setGrpSort(String(grp.sortOrder ?? ""));
        setGrpModalOpen(true);
    };

    const closeGrpModal = () => { setGrpModalOpen(false); setGrpEditing(null); };

    const saveGrp = async () => {
        const trimName = grpName.trim();
        if (!trimName) { setGrpNameErr(true); return; }
        setGrpNameErr(false);
        const sortNum = grpSort.trim() !== "" ? parseInt(grpSort, 10) : undefined;
        setGrpSaving(true);
        setLoading(true);
        try {
            if (grpEditing) {
                const updated = await patchPWFringeOutputGroup(grpEditing.pwFringeOutputGroupID, {
                    name: trimName,
                    payType: grpPay.trim() || null,
                    sortOrder: sortNum,
                });
                setGroups((prev) =>
                    prev?.map((g) =>
                        g.pwFringeOutputGroupID === grpEditing.pwFringeOutputGroupID
                            ? { ...g, ...updated }
                            : g
                    ) ?? prev
                );
                showToast("ok", `Output group <b>${trimName}</b> updated.`);
            } else {
                const created = await createPWFringeOutputGroup({
                    name: trimName,
                    payType: grpPay.trim() || null,
                    sortOrder: sortNum,
                });
                setGroups((prev) => (prev ? [...prev, created] : [created]));
                showToast("ok", `Output group <b>${trimName}</b> added.`);
            }
            closeGrpModal();
        } catch (err) {
            showToast("err", getErrorMessage(err));
        } finally {
            setGrpSaving(false);
            setLoading(false);
        }
    };

    // ── Delete output group (with client-side conflict check) ─────────────
    const openDeleteGrp = (grp: PWFringeOutputGroup) => {
        const conflict = (items ?? []).filter((f) =>
            [
                f.timecardGroupID,
                f.payrollExportGroupID,
                f.accountingExportGroupID,
                f.deductionPayrollExportGroupID,
            ].includes(grp.pwFringeOutputGroupID)
        );
        setDeleteModal({ group: grp, conflict });
    };

    const confirmDelete = async () => {
        if (!deleteModal || deleteModal.conflict.length > 0) return;
        setDeleting(true);
        try {
            await deletePWFringeOutputGroup(deleteModal.group.pwFringeOutputGroupID);
            setGroups((prev) =>
                prev?.filter(
                    (g) => g.pwFringeOutputGroupID !== deleteModal.group.pwFringeOutputGroupID
                ) ?? prev
            );
            showToast("ok", `Output group <b>${deleteModal.group.name}</b> deleted.`);
            setDeleteModal(null);
        } catch (err) {
            const isConflict = (err as { isConflict?: boolean }).isConflict;
            if (isConflict) {
                // Fallback for races: refresh and show error
                await loadGroups();
                showToast(
                    "err",
                    "This group is still assigned to fringe types. Reassign them first."
                );
                setDeleteModal(null);
            } else {
                showToast("err", getErrorMessage(err));
            }
        } finally {
            setDeleting(false);
        }
    };

    // ── Sortable th ───────────────────────────────────────────────────────
    const SortTh = ({ label, sk }: { label: string; sk: FtSortKey }) => {
        const on = sortKey === sk;
        return (
            <th
                className={`sortable${on ? " sorted" : ""}`}
                onClick={() => handleFtSort(sk)}
            >
                <span className="th-in">
                    {label}
                    <ChevronUp
                        className="caret"
                        size={13}
                        style={{
                            transform: on && sortDir === "desc" ? "scaleY(-1)" : undefined,
                        }}
                    />
                </span>
            </th>
        );
    };

    // ── Group select options ──────────────────────────────────────────────
    const GroupOptions = ({ value, onChange }: {
        value: number | null;
        onChange: (v: number | null) => void;
    }) => (
        <select
            className="pw2-select"
            style={{ width: "100%" }}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        >
            <option value="">— None —</option>
            {sortedGroups.map((g) => (
                <option key={g.pwFringeOutputGroupID} value={g.pwFringeOutputGroupID}>
                    {g.name}{g.payType ? ` (${g.payType})` : ""}
                </option>
            ))}
        </select>
    );

    // ── Render group cell ─────────────────────────────────────────────────
    const GrpCell = ({ id }: { id: number | null }) => {
        if (id === null) return <span className="dash">{DASH}</span>;
        const g = groupById(id);
        if (!g) return <span className="dash">{DASH}</span>;
        return (
            <div className="pw2-grp-cell">
                <span className="g-name">{g.name}</span>
                {g.payType && <span className="g-pay">{g.payType}</span>}
            </div>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <PageWrapper title="Fringe Types" backgroundColor="#f4f5f7">
            <div className="pwv2">
                <div className="pw2-page">
                    {/* Breadcrumb */}
                    <div className="pw2-crumbs">
                        <span>Prevailing Wage</span>
                        <ChevronRight size={14} />
                        <span className="here">Fringe Types</span>
                    </div>

                    {/* Header */}
                    <div className="pw2-app-head">
                        <div className="pw2-logo">
                            <BadgeDollarSign size={23} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <h1 className="pw2-app-title">Fringe Types</h1>
                            <div className="pw2-app-sub">
                                Certified-payroll fringe benefit types and their export output groups.
                            </div>
                        </div>
                        {!isReadOnly && (
                            <div className="pw2-head-action">
                                {tab === "types" ? (
                                    <button className="pw2-btn primary" onClick={openCreateFt}>
                                        <Plus size={16} />
                                        Add fringe type
                                    </button>
                                ) : (
                                    <button className="pw2-btn primary" onClick={openCreateGrp}>
                                        <Plus size={16} />
                                        Add output group
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Read-only note */}
                    {isReadOnly && (
                        <div className="pw2-ro-note">
                            <Info size={17} />
                            <span>
                                <b>Read-only on this environment.</b> Fringe types and output
                                groups are managed in the primary payroll system.
                            </span>
                        </div>
                    )}

                    {/* Tab bar */}
                    <div className="pw2-tabs">
                        <button
                            className={`pw2-tab${tab === "types" ? " on" : ""}`}
                            onClick={() => { setTab("types"); setPageNum(1); }}
                        >
                            <BadgeDollarSign size={17} />
                            Fringe Types
                            <span className="tcount">{activeTypeCount}</span>
                        </button>
                        <button
                            className={`pw2-tab${tab === "groups" ? " on" : ""}`}
                            onClick={() => setTab("groups")}
                        >
                            <Box size={17} />
                            Output Groups
                            <span className="tcount">{groupCount}</span>
                        </button>
                    </div>

                    {/* ── Fringe Types tab ── */}
                    {tab === "types" && (
                        <>
                            {/* Helper line */}
                            <div className="pw2-helper-line">
                                <Info size={15} />
                                <span>
                                    Output groups control where each fringe amount lands — on the
                                    timecard, and on the payroll &amp; accounting exports. Leave any
                                    unset to omit it there.
                                </span>
                            </div>

                            {/* Toolbar */}
                            <div className="pw2-toolbar">
                                <div className="pw2-segmented">
                                    <button
                                        className={filterActive === "active" ? "on" : ""}
                                        onClick={() => handleFtFilter("active")}
                                    >
                                        Active
                                    </button>
                                    <button
                                        className={filterActive === "inactive" ? "on" : ""}
                                        onClick={() => handleFtFilter("inactive")}
                                    >
                                        Inactive
                                    </button>
                                </div>
                            </div>

                            {/* Result line */}
                            <div className="pw2-result-line">
                                <span>
                                    <b>{total}</b>{" "}
                                    {total === 1 ? "fringe type" : "fringe types"}
                                </span>
                                <span className="rl-chip">
                                    {filterActive === "active" ? "Active" : "Inactive"}
                                </span>
                            </div>

                            {/* Table */}
                            <div className="pw2-card">
                                <div className="pw2-tbl-wrap">
                                    <table className="pw2-dt">
                                        <thead>
                                            <tr>
                                                <SortTh label="Name" sk="name" />
                                                <SortTh label="Description" sk="desc" />
                                                <th>Timecard Group</th>
                                                <th>Payroll Export Group</th>
                                                <th>Accounting Export Group</th>
                                                <th>Deduction Group</th>
                                                {!isReadOnly && <th className="right">Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {viewFt.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={isReadOnly ? 6 : 7}
                                                        style={{ padding: 0 }}
                                                    >
                                                        <div className="pw2-empty">
                                                            <div className="eic">
                                                                <BadgeDollarSign />
                                                            </div>
                                                            <div className="et">No fringe types found</div>
                                                            <p>
                                                                There are no{" "}
                                                                {filterActive === "inactive"
                                                                    ? "inactive"
                                                                    : "active"}{" "}
                                                                fringe types.
                                                            </p>
                                                            {filterActive === "inactive" && (
                                                                <button
                                                                    className="pw2-btn sm"
                                                                    style={{ marginTop: 6 }}
                                                                    onClick={() =>
                                                                        handleFtFilter("active")
                                                                    }
                                                                >
                                                                    <X size={14} />
                                                                    Show active
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                viewFt.map((row) => (
                                                    <tr
                                                        key={row.pwFringeTypeID}
                                                        className={
                                                            filterActive === "inactive"
                                                                ? "inactive-row"
                                                                : ""
                                                        }
                                                    >
                                                        <td>
                                                            <div className="pw2-name-cell">
                                                                <span
                                                                    className={`nm${filterActive === "inactive" ? " dim" : ""}`}
                                                                >
                                                                    {row.name}
                                                                </span>
                                                                {filterActive === "inactive" && (
                                                                    <span className="pw2-pill inactive">
                                                                        <span className="dot" />
                                                                        Inactive
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {row.description ? (
                                                                row.description
                                                            ) : (
                                                                <span className="dash">{DASH}</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <GrpCell id={row.timecardGroupID} />
                                                        </td>
                                                        <td>
                                                            <GrpCell id={row.payrollExportGroupID} />
                                                        </td>
                                                        <td>
                                                            <GrpCell id={row.accountingExportGroupID} />
                                                        </td>
                                                        <td>
                                                            <GrpCell id={row.deductionPayrollExportGroupID} />
                                                        </td>
                                                        {!isReadOnly && (
                                                            <td className="right">
                                                                <div className="pw2-row-act">
                                                                    <button
                                                                        className="pw2-linkbtn"
                                                                        onClick={() => openEditFt(row)}
                                                                    >
                                                                        <Edit size={14} />
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        className={`pw2-linkbtn${row.active ? " danger" : ""}`}
                                                                        onClick={() =>
                                                                            handleToggleFt(row)
                                                                        }
                                                                    >
                                                                        {row.active ? (
                                                                            <>
                                                                                <Slash size={14} />
                                                                                Deactivate
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Check size={14} />
                                                                                Activate
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {total > pageSize && (
                                    <div className="pw2-pager">
                                        <div className="count">
                                            Showing{" "}
                                            <b>
                                                {(safePage - 1) * pageSize + 1}–
                                                {Math.min(safePage * pageSize, total)}
                                            </b>{" "}
                                            of <b>{total}</b>
                                        </div>
                                        <div className="psize">
                                            Rows{" "}
                                            <select
                                                className="pw2-select"
                                                value={pageSize}
                                                onChange={(e) => {
                                                    setPageSize(Number(e.target.value));
                                                    setPageNum(1);
                                                }}
                                            >
                                                {PAGE_SIZES.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {pages > 1 && (
                                            <div className="pages">
                                                <button
                                                    className="pg nav"
                                                    disabled={safePage === 1}
                                                    onClick={() => setPageNum((p) => Math.max(1, p - 1))}
                                                >
                                                    <ChevronRight
                                                        size={16}
                                                        style={{ transform: "scaleX(-1)" }}
                                                    />
                                                </button>
                                                {pageWindow(safePage, pages).map((n, i) =>
                                                    n === "…" ? (
                                                        <span key={`el-${i}`} className="pg ellipsis">…</span>
                                                    ) : (
                                                        <button
                                                            key={n}
                                                            className={`pg${n === safePage ? " on" : ""}`}
                                                            onClick={() => setPageNum(n as number)}
                                                        >
                                                            {n}
                                                        </button>
                                                    )
                                                )}
                                                <button
                                                    className="pg nav"
                                                    disabled={safePage === pages}
                                                    onClick={() => setPageNum((p) => Math.min(pages, p + 1))}
                                                >
                                                    <ChevronRight size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* ── Output Groups tab ── */}
                    {tab === "groups" && (
                        <>
                            {/* Result line */}
                            <div className="pw2-result-line">
                                <span>
                                    <b>{sortedGroups.length}</b> output{" "}
                                    {sortedGroups.length === 1 ? "group" : "groups"}
                                </span>
                            </div>

                            <div className="pw2-card">
                                <div className="pw2-tbl-wrap">
                                    <table className="pw2-dt">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Pay Type</th>
                                                <th>Sort Order</th>
                                                {!isReadOnly && <th className="right">Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedGroups.length === 0 ? (
                                                <tr>
                                                    <td
                                                        colSpan={isReadOnly ? 3 : 4}
                                                        style={{ padding: 0 }}
                                                    >
                                                        <div className="pw2-empty">
                                                            <div className="eic"><Box /></div>
                                                            <div className="et">No output groups</div>
                                                            <p>
                                                                Output groups route fringe amounts to
                                                                timecards and exports.
                                                            </p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                sortedGroups.map((grp) => (
                                                    <tr key={grp.pwFringeOutputGroupID}>
                                                        <td>
                                                            <span className="strong">{grp.name}</span>
                                                        </td>
                                                        <td>
                                                            {grp.payType ? (
                                                                <span className="pw2-paytype-chip">
                                                                    {grp.payType}
                                                                </span>
                                                            ) : (
                                                                <span className="dash">{DASH}</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="mono">{grp.sortOrder}</span>
                                                        </td>
                                                        {!isReadOnly && (
                                                            <td className="right">
                                                                <div className="pw2-row-act">
                                                                    <button
                                                                        className="pw2-linkbtn"
                                                                        onClick={() => openEditGrp(grp)}
                                                                    >
                                                                        <Edit size={14} />
                                                                        Edit
                                                                    </button>
                                                                    <button
                                                                        className="pw2-linkbtn danger"
                                                                        onClick={() => openDeleteGrp(grp)}
                                                                    >
                                                                        <Trash2 size={14} />
                                                                        Delete
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                    {/* ── Fringe type modal ── */}
                    {ftModalOpen && (
                        <div className="pw2-modal-bg" onClick={closeFtModal}>
                            <div className="pw2-modal wide" onClick={(e) => e.stopPropagation()}>
                                <div className="pw2-modal-head">
                                    <span className="mi">
                                        <BadgeDollarSign size={21} />
                                    </span>
                                    <div>
                                        <h3>{ftEditing ? "Edit fringe type" : "Add fringe type"}</h3>
                                        <div className="ms">
                                            {ftEditing
                                                ? ftEditing.name
                                                : "Fringe benefit tracked on certified payroll."}
                                        </div>
                                    </div>
                                    <button className="x" onClick={closeFtModal}>
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="pw2-modal-body">
                                    <div className="pw2-frow2">
                                        <div className="pw2-frow">
                                            <label>Name <span className="req">*</span></label>
                                            <input
                                                className={`pw2-input${ftNameErr ? " err" : ""}`}
                                                value={ftName}
                                                placeholder="e.g. Health & Welfare"
                                                onChange={(e) => {
                                                    setFtName(e.target.value);
                                                    if (ftNameErr && e.target.value.trim())
                                                        setFtNameErr(false);
                                                }}
                                                autoFocus
                                            />
                                            {ftNameErr && (
                                                <div className="pw2-inline-err">
                                                    <AlertTriangle size={15} />
                                                    Name is required.
                                                </div>
                                            )}
                                        </div>
                                        <div className="pw2-frow">
                                            <label>Sort order <span className="opt">optional</span></label>
                                            <input
                                                className="pw2-input"
                                                type="number"
                                                value={ftSort}
                                                placeholder="10"
                                                onChange={(e) => setFtSort(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="pw2-frow">
                                        <label>Description <span className="opt">optional</span></label>
                                        <textarea
                                            className="pw2-textarea"
                                            value={ftDesc}
                                            placeholder="Short description."
                                            onChange={(e) => setFtDesc(e.target.value)}
                                        />
                                    </div>
                                    <div className="pw2-divider" />
                                    <div className="pw2-subhead">Output groups</div>
                                    <div className="pw2-helper-line" style={{ margin: "0 0 14px" }}>
                                        <Info size={15} />
                                        <span>
                                            Output groups control where this fringe amount lands — on the
                                            timecard, and on the payroll &amp; accounting exports. Leave
                                            any unset to omit it there.
                                        </span>
                                    </div>
                                    <div className="pw2-frow3">
                                        <div className="pw2-frow">
                                            <label>Timecard</label>
                                            <GroupOptions value={ftTC} onChange={setFtTC} />
                                        </div>
                                        <div className="pw2-frow">
                                            <label>Payroll export</label>
                                            <GroupOptions value={ftPE} onChange={setFtPE} />
                                        </div>
                                        <div className="pw2-frow">
                                            <label>Accounting export</label>
                                            <GroupOptions value={ftAE} onChange={setFtAE} />
                                        </div>
                                    </div>
                                    <div className="pw2-divider" />
                                    <div className="pw2-subhead">Payroll deduction</div>
                                    <div className="pw2-helper-line" style={{ margin: "0 0 14px" }}>
                                        <Info size={15} />
                                        <span>
                                            Set this only when the fringe is remitted to the union rather
                                            than paid to the employee — payroll receives an offsetting
                                            pre-tax deduction under this group's pay type. Choosing the same
                                            group as the payroll export makes the deduction cancel the
                                            earning exactly. Applies wherever this fringe is earned —
                                            prevailing wage projects, union classifications and catch-up
                                            alike.
                                        </span>
                                    </div>
                                    <div className="pw2-frow3">
                                        <div className="pw2-frow">
                                            <label>Deduction</label>
                                            <GroupOptions value={ftDG} onChange={setFtDG} />
                                        </div>
                                    </div>
                                </div>
                                <div className="pw2-modal-foot">
                                    <button className="pw2-btn ghost" onClick={closeFtModal} disabled={ftSaving}>
                                        Cancel
                                    </button>
                                    <button className="pw2-btn primary" onClick={saveFt} disabled={ftSaving}>
                                        <Check size={16} />
                                        {ftEditing ? "Save changes" : "Add fringe type"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Output group modal ── */}
                    {grpModalOpen && (
                        <div className="pw2-modal-bg" onClick={closeGrpModal}>
                            <div className="pw2-modal" onClick={(e) => e.stopPropagation()}>
                                <div className="pw2-modal-head">
                                    <span className="mi"><Box size={21} /></span>
                                    <div>
                                        <h3>{grpEditing ? "Edit output group" : "Add output group"}</h3>
                                        <div className="ms">
                                            {grpEditing
                                                ? grpEditing.name
                                                : "A destination for fringe amounts on exports."}
                                        </div>
                                    </div>
                                    <button className="x" onClick={closeGrpModal}>
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="pw2-modal-body">
                                    <div className="pw2-frow">
                                        <label>Name <span className="req">*</span></label>
                                        <input
                                            className={`pw2-input${grpNameErr ? " err" : ""}`}
                                            value={grpName}
                                            placeholder="e.g. Fringe — Trust"
                                            onChange={(e) => {
                                                setGrpName(e.target.value);
                                                if (grpNameErr && e.target.value.trim())
                                                    setGrpNameErr(false);
                                            }}
                                            autoFocus
                                        />
                                        {grpNameErr && (
                                            <div className="pw2-inline-err">
                                                <AlertTriangle size={15} />
                                                Name is required.
                                            </div>
                                        )}
                                    </div>
                                    <div className="pw2-frow2">
                                        <div className="pw2-frow">
                                            <label>Pay type <span className="opt">optional</span></label>
                                            <input
                                                className="pw2-input"
                                                value={grpPay}
                                                placeholder="e.g. Cash or Trust"
                                                onChange={(e) => setGrpPay(e.target.value)}
                                            />
                                        </div>
                                        <div className="pw2-frow">
                                            <label>Sort order <span className="opt">optional</span></label>
                                            <input
                                                className="pw2-input"
                                                type="number"
                                                value={grpSort}
                                                placeholder="10"
                                                onChange={(e) => setGrpSort(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="pw2-modal-foot">
                                    <button className="pw2-btn ghost" onClick={closeGrpModal} disabled={grpSaving}>
                                        Cancel
                                    </button>
                                    <button className="pw2-btn primary" onClick={saveGrp} disabled={grpSaving}>
                                        <Check size={16} />
                                        {grpEditing ? "Save changes" : "Add output group"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Delete group confirm / conflict modal ── */}
                    {deleteModal && (
                        <div className="pw2-modal-bg" onClick={() => setDeleteModal(null)}>
                            <div className="pw2-modal" onClick={(e) => e.stopPropagation()}>
                                <div className={`pw2-modal-head${deleteModal.conflict.length === 0 ? " danger" : ""}`}>
                                    <span className="mi">
                                        {deleteModal.conflict.length > 0 ? (
                                            <AlertTriangle size={21} />
                                        ) : (
                                            <Trash2 size={21} />
                                        )}
                                    </span>
                                    <div>
                                        <h3>
                                            {deleteModal.conflict.length > 0
                                                ? "Can't delete this output group"
                                                : "Delete output group?"}
                                        </h3>
                                        <div className="ms">{deleteModal.group.name}</div>
                                    </div>
                                    <button className="x" onClick={() => setDeleteModal(null)}>
                                        <X size={16} />
                                    </button>
                                </div>
                                <div className="pw2-modal-body">
                                    {deleteModal.conflict.length > 0 ? (
                                        <>
                                            <div className="pw2-err-banner">
                                                <AlertTriangle size={17} />
                                                <div>
                                                    <b>
                                                        This group is still assigned to{" "}
                                                        {deleteModal.conflict.length}{" "}
                                                        fringe{" "}
                                                        {deleteModal.conflict.length === 1
                                                            ? "type"
                                                            : "types"}.
                                                    </b>{" "}
                                                    Reassign{" "}
                                                    {deleteModal.conflict.length === 1
                                                        ? "it"
                                                        : "them"}{" "}
                                                    before deleting.
                                                </div>
                                            </div>
                                            <div className="pw2-confirm-summary">
                                                Assigned to:{" "}
                                                {deleteModal.conflict
                                                    .map((f) => f.name)
                                                    .join(", ")}
                                                .
                                            </div>
                                        </>
                                    ) : (
                                        <div className="pw2-confirm-summary">
                                            Permanently delete{" "}
                                            <b>{deleteModal.group.name}</b>? This can't be undone.
                                            Fringe types don't reference this group, so nothing else
                                            is affected.
                                        </div>
                                    )}
                                </div>
                                <div className="pw2-modal-foot">
                                    {deleteModal.conflict.length > 0 ? (
                                        <button className="pw2-btn" onClick={() => setDeleteModal(null)}>
                                            Close
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                className="pw2-btn ghost"
                                                onClick={() => setDeleteModal(null)}
                                                disabled={deleting}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="pw2-btn danger-solid"
                                                onClick={confirmDelete}
                                                disabled={deleting}
                                            >
                                                <Trash2 size={16} />
                                                Delete group
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Toasts */}
                    {toasts.length > 0 && (
                        <div className="pw2-toast-wrap">
                            {toasts.map((t) => (
                                <div
                                    key={t.id}
                                    className={`pw2-toast ${t.kind}${t.out ? " out" : ""}`}
                                >
                                    {t.kind === "ok" ? (
                                        <CheckCircle size={18} />
                                    ) : (
                                        <AlertTriangle size={18} />
                                    )}
                                    <span
                                        className="t-msg"
                                        dangerouslySetInnerHTML={{ __html: t.msg }}
                                    />
                                    {t.undoFn && (
                                        <button
                                            className="t-undo"
                                            onClick={() => {
                                                setToasts((prev) =>
                                                    prev.filter((x) => x.id !== t.id)
                                                );
                                                t.undoFn!();
                                            }}
                                        >
                                            Undo
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </PageWrapper>
    );
};

export default PWFringeTypesPage;
